import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Minus } from 'lucide-react';
import { productsAPI, cartAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      const data = await productsAPI.getById(id);
      setProduct(data);
      if (data.sizes.length > 0) {
        setSelectedSize(data.sizes[0]);
      }
    } catch (error) {
      console.error('Failed to load product:', error);
      navigate('/catalog');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }

    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/auth');
      return;
    }

    try {
      await cartAPI.add(product._id, selectedSize, quantity);
      toast.success('Added to cart');
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-2xl font-bold tracking-wider">LOADING...</p>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-white pt-20">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-bold tracking-wider mb-12 hover:opacity-50 transition-opacity"
        >
          <ArrowLeft size={18} strokeWidth={2.5} />
          BACK
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Product Image */}
          <div className="aspect-[3/4] bg-gray-100 relative">
            <div
              className="absolute inset-0"
              style={{ backgroundColor: product.colorBlock }}
            />
            {!product.inStock && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                <span className="text-2xl font-bold tracking-wider">SOLD OUT</span>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter mb-4">
                {product.name}
              </h1>
              <p className="text-2xl md:text-3xl font-bold">${product.price}</p>
            </div>

            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              {product.description}
            </p>

            {/* Size Selection */}
            <div className="mb-8">
              <h3 className="text-sm font-bold tracking-wider mb-4">SIZE</h3>
              <div className="flex flex-wrap gap-3">
                {product.sizes.map(size => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    disabled={!product.inStock}
                    className={`w-16 h-16 border-2 text-sm font-bold tracking-wider transition-colors ${
                      selectedSize === size
                        ? 'border-black bg-black text-white'
                        : 'border-black hover:bg-black hover:text-white'
                    } disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-black`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-12">
              <h3 className="text-sm font-bold tracking-wider mb-4">QUANTITY</h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={!product.inStock}
                  className="w-12 h-12 border-2 border-black flex items-center justify-center hover:bg-black hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Minus size={18} strokeWidth={2.5} />
                </button>
                <span className="text-xl font-bold w-12 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  disabled={!product.inStock}
                  className="w-12 h-12 border-2 border-black flex items-center justify-center hover:bg-black hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Plus size={18} strokeWidth={2.5} />
                </button>
              </div>
            </div>

            {/* Add to Cart */}
            <button
              onClick={handleAddToCart}
              disabled={!product.inStock}
              className="w-full bg-black text-white py-6 text-sm font-bold tracking-wider hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {product.inStock ? 'ADD TO CART' : 'SOLD OUT'}
            </button>

            {/* Product Details */}
            <div className="mt-12 pt-12 border-t border-gray-200 space-y-6">
              <div>
                <h4 className="text-sm font-bold tracking-wider mb-2">DETAILS</h4>
                <p className="text-sm text-gray-600">
                  Premium construction with attention to every detail. Made to last.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-bold tracking-wider mb-2">SHIPPING</h4>
                <p className="text-sm text-gray-600">
                  Free worldwide shipping on orders over $500. Express delivery available.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-bold tracking-wider mb-2">RETURNS</h4>
                <p className="text-sm text-gray-600">
                  30-day return policy. Items must be unworn and in original packaging.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
